import { NgModule } from "@angular/core";
import {
    RouterModule,
    Routes,
    UrlMatchResult,
    UrlSegment,
    UrlSegmentGroup,
} from "@angular/router";

import {
    isAppleWatch,
    isBeltkit,
    isBikini,
    isDoubleFragance,
    isDoubleObject,
    isGiftSet,
    isLook,
    isSimpleProduct,
    urlSkuToSku,
} from "@hermes/utils-generic/helpers";

import { ProductPageComponent } from "./components/product-page/product-page.component";
import { ProductPageGuard } from "./guards/product-page.guard";

export function matchSimpleRoute(
    url: UrlSegment[],
    group: UrlSegmentGroup,
): UrlMatchResult {
    return matchProductRoute(group.segments, url, isSimpleProduct);
}

export function matchBeltkitRoute(
    url: UrlSegment[],
    group: UrlSegmentGroup,
): UrlMatchResult {
    return matchProductRoute(group.segments, url, isBeltkit);
}

export function matchAppleWatchRoute(
    url: UrlSegment[],
    group: UrlSegmentGroup,
): UrlMatchResult {
    return matchProductRoute(group.segments, url, isAppleWatch);
}

export function matchGiftsetRoute(
    url: UrlSegment[],
    group: UrlSegmentGroup,
): UrlMatchResult {
    return matchProductRoute(group.segments, url, isGiftSet);
}

export function matchDoubleFragranceRoute(
    url: UrlSegment[],
    group: UrlSegmentGroup,
): UrlMatchResult {
    return matchProductRoute(group.segments, url, isDoubleFragance);
}

export function matchLookRoute(
    url: UrlSegment[],
    group: UrlSegmentGroup,
): UrlMatchResult {
    return matchProductRoute(group.segments, url, isLook);
}

export function matchDoubleObjectRoute(
    url: UrlSegment[],
    group: UrlSegmentGroup,
): UrlMatchResult {
    return (
        matchProductRoute(group.segments, url, isDoubleObject) ||
        matchProductRoute(group.segments, url, isBikini)
    );
}

export function matchProductRoute(
    allSegments: UrlSegment[],
    remainingSegments: UrlSegment[],
    skuParser: (sku: string) => boolean,
): UrlMatchResult {
    const urlSku: string = allSegments[1]?.path.split("-").pop();
    if (urlSku && skuParser(urlSkuToSku(urlSku))) {
        return { consumed: remainingSegments };
    }
    return undefined;
}

const routes: Routes = [":slug/", ":slug/:skuPart/"].map((path) => ({
    path,
    component: ProductPageComponent,
    canActivate: [ProductPageGuard],
    children: [
        {
            matcher: matchSimpleRoute,
            loadChildren: () =>
                import("./simple/simple-product-page.module").then(
                    (m) => m.SimpleProductPageModule,
                ),
        },
        {
            matcher: matchBeltkitRoute,
            loadChildren: () =>
                import("./beltkit/beltkit-product-page.module").then(
                    (m) => m.BeltkitProductPageModule,
                ),
        },
        {
            matcher: matchAppleWatchRoute,
            loadChildren: () =>
                import("./apple-watch/apple-watch-product-page.module").then(
                    (m) => m.AppleWatchProductPageModule,
                ),
        },
        {
            matcher: matchGiftsetRoute,
            loadChildren: () =>
                import("./giftset/giftset-product-page.module").then(
                    (m) => m.GiftSetProductPageModule,
                ),
        },
        {
            matcher: matchDoubleFragranceRoute,
            loadChildren: () =>
                import(
                    "./double-fragrance/double-fragrance-product-page.module"
                ).then((m) => m.DoubleFragranceProductPageModule),
        },
        {
            matcher: matchLookRoute,
            loadChildren: () =>
                import("./look/look-product-page.module").then(
                    (m) => m.LookProductPageModule,
                ),
        },
        {
            matcher: matchDoubleObjectRoute,
            loadChildren: () =>
                import("./bikini/bikini-product-page.module").then(
                    (m) => m.BikiniProductPageModule,
                ),
        },
    ],
}));

@NgModule({
    imports: [RouterModule.forChild(routes)],
    exports: [RouterModule],
    providers: [ProductPageGuard],
})
export class ProductPageRoutingModule {}
